// pages/Shop/ShopDetails.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaStore, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaEdit, FaBuilding, FaCalendarAlt, FaChevronDown } from 'react-icons/fa';
import './Shop.css';

const ShopDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shop } = location.state || {};

  const BASE_URL = 'https://super-backend.hira-tech.co.uk';

  const [selectedCity, setSelectedCity] = useState('');

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  let shopCities = [];
  if (shop && shop.cities) {
    if (typeof shop.cities === 'string') {
      try {
        shopCities = JSON.parse(shop.cities);
      } catch {
        shopCities = shop.cities.split(',').map(c => c.trim()).filter(Boolean);
      }
    } else if (Array.isArray(shop.cities)) {
      shopCities = shop.cities;
    }
  }

  if (!shop) {
    return (
      <div className="shop-details-container">
        <div className="error-state">
          <p>No shop data available</p>
          <button onClick={() => navigate('/shops')} className="back-to-shops">
            Back to Shops
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="shop-details-container">
      <div className="shop-details-header">
        <div className="header-left-spacer"></div>
        <div className="shop-header-actions">
          <button className="back-button" onClick={() => navigate('/shops')}>
            Back to Shops
          </button>
        </div>
      </div>

      <div className="shop-details-content">
        <div className="shop-details-title">
          <div className="shop-logo-large">
            {shop.shop_logo ? (
              <img
                src={getImageUrl(shop.shop_logo)}
                alt={shop.shop_name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<div class="logo-placeholder-large"><FaStore /></div>`;
                }}
              />
            ) : (
              <div className="logo-placeholder-large">
                <FaStore />
              </div>
            )}
          </div>
          <div className="shop-title-info">
            <h1>{shop.shop_name}</h1>
            <div className="shop-status">
              <span className={`status-badge-large ${shop.is_active ? 'active' : 'inactive'}`}>
                {shop.is_active ? <FaCheckCircle /> : <FaTimesCircle />}
                <span>{shop.is_active ? 'Active' : 'Inactive'}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="details-grid">
          <div className="detail-card">
            <h3>
              <FaBuilding className="card-icon" />
              Basic Information
            </h3>
            <div className="detail-item">
              <strong>Shop ID:</strong>
              <span>{shop.shop_id}</span>
            </div>
            <div className="detail-item">
              <strong>Shop Name:</strong>
              <span>{shop.shop_name}</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>
              <FaEnvelope className="card-icon" />
              Contact Information
            </h3>
            <div className="detail-item">
              <strong>Email:</strong>
              <span>{shop.shop_email || 'Not provided'}</span>
            </div>
            <div className="detail-item">
              <strong>Phone:</strong>
              <span>{shop.shop_phone || 'Not provided'}</span>
            </div>
            <div className="detail-item">
              <strong>Address:</strong>
              <span>{shop.shop_address || 'Not provided'}</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>
              <FaMapMarkerAlt className="card-icon" />
              Cities / Country
            </h3>

            {shopCities.length > 0 ? (
              <div className="detail-item" style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-start' }}>
                <strong style={{ width: '100%' }}>Cities:</strong>
                <div className="details-city-dropdown-wrapper" style={{ width: '100%', marginTop: '5px' }}>
                  <select
                    className="details-city-select"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    style={{ width: '100%', background: '#f8faff', border: '1px solid #004aad', borderRadius: '4px', padding: '8px 12px', color: '#333', fontWeight: '600', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', cursor: 'pointer' }}
                  >
                    <option value="">{shopCities.length > 1 ? 'All Cities' : shopCities[0]}</option>
                    {shopCities.map((city, index) => (
                      <option key={index} value={city}>{city}</option>
                    ))}
                  </select>
                  <span className="details-dropdown-arrow" style={{ right: '12px', pointerEvents: 'none', position: 'absolute', top: '50%', transform: 'translateY(-50%)', color: '#004aad', fontSize: '0.8rem' }}>
                    ▼
                  </span>
                </div>
              </div>
            ) : (
              <div className="detail-item">
                <span>No cities selected</span>
              </div>
            )}

            <div className="detail-item" style={{ marginTop: '15px', borderTop: '1px solid #f0f0f0', paddingTop: '15px' }}>
              <strong>Country:</strong>
              <span>{shop.country || 'Not specified'}</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>
              <FaCalendarAlt className="card-icon" />
              System Information
            </h3>
            <div className="detail-item">
              <strong>Created At:</strong>
              <span>{formatDate(shop.created_at)}</span>
            </div>
            <div className="detail-item">
              <strong>Last Updated:</strong>
              <span>{formatDate(shop.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopDetails;